/* eslint-disable jsdoc/no-undefined-types */
import vuex from 'vuex';
import get from 'lodash.get';

import isString from '#shared/util/isString.js';
import { decode as decodeAswparam } from '#shared/util/aswparam.js';
import loadExternalLinkHandlerMiddleware from '#shared/middleware/loadExternalLinkHandlerMiddleware.js';
import loadDeveloperMiddleware from '#shared/middleware/loadDeveloperMiddleware.js';
import loadRegionConfigMiddleware from '#shared/middleware/loadRegionConfigMiddleware.js';
import loadLocalizationMiddleware from '#shared/middleware/loadLocalizationMiddleware.js';
import loadInitialStateMiddleware from '#shared/middleware/loadInitialStateMiddleware.js';

import cError from '#themes/default/default/components/error/error.js';

import pageHeader from '#frontend/util/pageHeader.js';

import './crucial.css' with { type: 'css' };
import template from './global.html' with { type: 'html' };

if (IS_CLIENT) {
	await import('#frontend/util/avMacPolyfill.js');
}

/**
 * @memberof PaymentMethods
 * @feature Bancontact [GEN]
 * @feature Boleto [GEN]
 * @feature CreditCard [GEN]
 * @feature DirectDebit [GEN]
 * @feature iDeal [GEN]
 * @feature Payconiq [GEN]
 * @feature PayPal [GEN]
 * @feature WireTransfer [GEN]
 */

/**
 * @memberof OtherPages
 * @feature Payment gateway iframe/return for 3DS auth [GEN]
 * @feature Error page - status code based [GEN]
 * @feature Status page /status [GEN]
 * @feature Flush cache [GEN]
 */

/**
 * @memberof Global
 * @feature Custom page `<title />` [GEN]
 * @feature GTM with correct ID [GEN]
 * @feature GTM SingleDataLayer with correct ID [GEN]
 * @feature Debug bar (Firebug console for QA Testing) [GEN]
 * @feature Implement tracking for each checkout step [GEN]
 * @feature Legal statement, policy links and copyright in footer [GEN]
 * @feature Mac products support through polyfill [GEN]
 * @feature Open all _blank links in browser [GEN]
 * @feature Notification bar - JS/Cookies disabled or outdated OS version [GEN]
 * @feature Body zoom - prevents issues with different window sizes [GEN]
 */

/**
 * @memberof ExternalDependency
 * @provider Gen Digital, Inc.
 * @script https://test-static.norton.com/estore/hpp/avast/qa/hpp_avast.js
 * @script https://buy-static.norton.com/estore/hpp/avast/hpp_avast.min.js
 * @usage Required to complete orders.
 * @usage Library is used to capture sensitive payment information in secured context using iframes hosted on providers infrastructure.
 */

export default {
	name: 'CGlobal',
	middleware: [
		loadExternalLinkHandlerMiddleware,
		loadDeveloperMiddleware,
		loadRegionConfigMiddleware,
		loadInitialStateMiddleware,
		loadLocalizationMiddleware
	],
	components: {
		cError
	},
	meta() {
		return {
			title: this.t('global_metaTitle'),
			script: {
				adobeAnalytics: this.theme.tracking.disabled || !this.theme.tracking.adobeAnalytics ? '<!-- ADOBE ANALYTICS removed by trackingDisabled option -->' : `<script defer src="https://assets.adobedtm.com/b29989a14bed/${this.theme.tracking.adobeAnalytics[this.app.env]}.min.js"></script>`,
				// eslint-disable-next-line max-len
				gtm: this.theme.tracking.disabled ? '<!-- GTM removed by trackingDisabled option -->' : `<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${this.theme.tracking.gtm}');</script>`,
				// eslint-disable-next-line max-len
				gtmSDL: this.theme.tracking.disabled ? '<!-- GTM SDL removed by trackingDisabled option -->' : `<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','sdl','${this.theme.tracking.gtmSDL}');</script>`,
				debug: this.order?.isDebug ? '<script id="Firebug" src="/static/default/js/firebug-lite--6-151-0-2529.js"></script><script src="/static/default/js/debugBar--6-151-0-2529.js"></script>' : '',
				// Push an information about consentGroups before GTM code is called
				consent: `<script>
					window.dataLayer = window.dataLayer || [];
					window.dataLayer.push({
						consentGroups: '${this.theme.tracking.consentGroups || this.defaultConsentGroups}'
					});
				</script>`,
				providersLibrary: this.provider?.variables.paymentLibrary ? `<script defer src="${this.provider.variables.paymentLibrary}"></script>` : ''
			},
			noscript: {
				gtm: this.theme.tracking.disabled ? '' : `<iframe src="https://www.googletagmanager.com/ns.html?id=${this.theme.tracking.gtm}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
				gtmSDL: this.theme.tracking.disabled ? '' : `<iframe src="https://www.googletagmanager.com/ns.html?id=${this.theme.tracking.gtmSDL}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
			},
			link: {
				providersPreconnect: this.providersPreconnect
			}
		};
	},
	data() {
		return {
			defaultConsentGroups: 'C0001:1,C0002:0,C0003:0,C0004:0,C0005:0',
			compatibility: '',
			paymentContainer: null,
			isCookieEnabled: true,
			isSmallDevice: false,
			offerCookie: null,
			offerCookieName: 'js-offerModal',
			scaCounter: 0,
			checkoutFormLocked: false
		};
	},
	computed: {
		...vuex.mapState([
			'account',
			'app',
			'connectionIsOnline',
			'customer',
			'error',
			'form',
			'global',
			'isCompany',
			'isEmpty',
			'isLoading',
			'isNewCart',
			'isNewMethod',
			'modal',
			'order',
			'provider',
			'providerAccount',
			'page',
			'payment',
			'retargetingUrl',
			'selectedPayment',
			'shouldGetOffer',
			'terms',
			'theme',
			'url',
			'polyfills'
		]),
		isAuthenticated() {
			return this.order?.isAuthenticated;
		},
		isGuest() {
			return this.order?.isGuest;
		},
		isPreselection() {
			return this.order?.hasTrialProduct && this.isNewCart && this.isGuest && !this.order.isBrazilianFlow;
		},
		isShoppingCartPage() {
			return this.page?.number === 0;
		},
		isCompanyDetailsPage() {
			return this.page?.number === 1;
		},
		isConfirmOrderPage() {
			return this.page?.number === 3;
		},
		hasDisclaimer() {
			return this.order?.hasDisclaimer;
		},
		isThankYouPage() {
			return this.page?.number === 4;
		},
		getPageHeadline() {
			return pageHeader({
				isNewCart: this.isNewCart,
				isNewMethod: this.isNewMethod,
				order: this.order,
				page: this.page,
				payment: this.payment,
				t: this.t
			});
		},
		getPageName() {
			if (this.isPreselection) {
				return 'Payment Selector';
			} else if (this.isAuthenticated && this.isShoppingCartPage) {
				return '1-click';
			} else {
				return this.page.tracking;
			}
		},
		providersPreconnect() {
			const sCodePreconnect = this.theme.tracking.adobeAnalytics ? `<link ref="prefetch" href="https://nortonlifelock.com/${this.theme.tracking.adobeAnalytics.scode[this.app.env]}" type="javascript" as="script" />` : '';
			return {
				gen: `
					<link rel="preconnect" href="https://fpt.norton.com" crossorigin />
					<link rel="preconnect" href="https://buy-static.norton.com" crossorigin />
					<link rel="preconnect" href="https://nortonlifelock.com" />
					<link rel="dns-prefetch" href="https://fpt.norton.com" />
					<link rel="dns-prefetch" href="https://buy-static.norton.com" />
					<link rel="dns-prefetch" href="https://nortonlifelock.com" />
					${sCodePreconnect}
				`
			}[this.theme?.provider?.name] || '';
		}
	},
	watch: {
		async isNewMethod() {
			await this.$nextTick();
			this.paymentContainer = this.$refs.paymentContainer;
		},
		async isAuthenticated() {
			await this.$nextTick();
			this.paymentContainer = this.$refs.paymentContainer;
		},
		async isGuest() {
			await this.$nextTick();
			this.paymentContainer = this.$refs.paymentContainer;
		},
		isLoading(loading) {
			if (loading) {
				document.activeElement.blur();
				document.addEventListener('keydown', this.blockTabs);
			} else {
				document.removeEventListener('keydown', this.blockTabs);
			}
		},
		$route() {
			this.utils().recomputeOfferProperty();
		},
		selectedPayment() {
			// Set company flow (if there is a business product)
			if (this.order.hasBusinessProduct && !this.order.isBrazilianFlow) {
				this.$store.commit('SET_COMPANY', this.selectedPayment.isCompany);
			}
		}
	},
	async mounted() {
		this.isCookieEnabled = this.cookieStorage._helpers.isCookieEnabled;
		await this.waitUntil([() => !this.isEmpty || this.page?.isBlankPage], 60 * 1000);
		this.paymentContainer = this.$refs.paymentContainer;
		if (!this.error) {
			this.tracking.virtualPageview({
				order: this.order,
				page: {
					...this.page,
					tracking: this.getPageName
				},
				theme: this.theme,
				url: this.url
			});
		}
		if (!this.isEmpty) {
			await this.updateUrlQuery();
			this.restoreForm('customer');
			this.restoreForm('company');
			this.restoreForm('form');
		}
	},
	methods: {
		blockTabs(event) {
			// Block tab key actions (while loading)
			if (event.keyCode === 9) {
				event.preventDefault();
			}
		},
		handleFormUpdate() {
			if (this.isShoppingCartPage || this.isCompanyDetailsPage) {
				this.rememberForm('customer', 'SET_CUSTOMER', this.customer);
				this.rememberForm('form', 'SET_FORM', this.form);
			}
		},
		async handleFormSubmit() {
			if (this.checkoutFormLocked) {
				return;
			}
			this.checkoutFormLocked = true;
			try {
				// Run checkout form action for current page (we assume that this method is implemented for every page with checkout form handler)
				const activePageComponent = this.$router.currentRoute.matched.at(0).instances.default;
				await activePageComponent.handleCheckoutFormSubmit();
			} catch {
				// Submit errors are handled in each of page checkout form submit handlers
			}
			this.checkoutFormLocked = false;
		},
		utils() {
			return {
				showLoader: () => {
					this.$store.commit('SET_LOADER', true);
				},
				hideLoader: () => {
					this.$store.commit('SET_LOADER', false);
				},
				setPaymentFailed: (error) => {
					const failureCode = get(error, 'app.detail.response.body.error.app.detail.errorCode', null);
					let providerAccountPaymentOptions;
					// Set used provider account payment option as failed (one-click)
					if (Array.isArray(this.payment.providerAccountPaymentOptions)) {
						providerAccountPaymentOptions = this.payment.providerAccountPaymentOptions.map((paymentOption) => {
							if (paymentOption.id === this.selectedPayment.id) {
								return {
									...paymentOption,
									failed: true,
									failureCode
								};
							}
							return paymentOption;
						});
					}
					this.$store.commit('SET_PAYMENT', Object.assign({
						...this.payment,
						failed: true,
						failureCode
					}, providerAccountPaymentOptions ? {
						providerAccountPaymentOptions
					} : {}));
				},
				unsetPaymentFailed: () => {
					this.$store.commit('SET_PAYMENT', {
						...this.payment,
						failed: false
					});
				},
				getResponse: async (action, payload = {}) => {
					try {
						const response = await this.$store.dispatch(action, payload);
						return response;
					} catch (error) {
						if (error.app.status === 401) {
							this.$store.commit('SET_ERROR', error);
						}
						throw error;
					}
				},
				applyBilling: async (billing) => {
					await this.utils().getResponse('applyBilling', billing);
				},
				removeBilling: async () => {
					await this.utils().getResponse('removeBilling');
				},
				applyCompany: async (company) => {
					await this.utils().getResponse('applyCompany', company);
				},
				applyForm: async (form) => {
					await this.utils().getResponse('applyForm', form);
				},
				applyPayment: async (paymentSource = {}) => {
					// If payment gateway return url was not provided, use default one optimized for in-iframe flow (eg. for credit card SCA)
					paymentSource.returnUrl = paymentSource.returnUrl || this.getLink('payment-gateway-return', null);
					await this.utils().getResponse('applyPayment', paymentSource);
				},
				submitOrder: async (skipTracking = false) => {
					if (!skipTracking) {
						this.tracking.submitOrder({
							order: this.order,
							payment: this.payment,
							url: this.url
						});
					}
					try {
						const submitOrderPayload = {};
						// Load transfer tooken cookie value created by GTM/SDL
						const transferTokenString = this.cookieStorage.getItem('TR_TOKEN');
						const transferTokenObject = decodeAswparam(transferTokenString || '');
						submitOrderPayload.affiliate = transferTokenObject.aff || {};
						const submitOrderResponse = await this.$store.dispatch('submitOrder', submitOrderPayload);
						if (isString(submitOrderResponse.data.paymentUrl)) {
							location.href = submitOrderResponse.data.paymentUrl;
							return null;
						}
						return submitOrderResponse;
					} catch (error) {
						const errorDetail = get(error, 'app.detail.response.body.error.app.detail', {});
						const userActionRequired = error.app.status === 402 && (errorDetail.nextAction || errorDetail.pendingRedirect);
						if (userActionRequired && this.payment.isRedirectable) {
							location.href = errorDetail.nextAction ?? errorDetail.pendingRedirect;
							return null;
						}
						if (userActionRequired && this.payment.type === 'payconiq') {
							const { qrCodeData, urlIntent } = errorDetail.eStore;
							this.utils().showPayconiqModal({
								qrCodeData,
								urlIntent
							});
							try {
								await this.websocket.emit('/ws/v1/cart/check-order-state');
								this.utils().hideModal();
								return this.utils().submitOrder(true);
							} catch {
								// Let it continue to the error handling below
							}
						} else if (userActionRequired && this.scaCounter < 5) {
							this.scaCounter++;
							await this.providersLibrary.handleNextAction(errorDetail.nextAction);
							return this.utils().submitOrder(true);
						}
						this.tracking.paymentAuthorisationFailed({
							app: this.app,
							order: this.order,
							page: this.page,
							url: this.url,
							theme: this.theme
						}, {
							...errorDetail,
							paymentMethod: this.payment.name
						});
						if (error.app.status === 401) {
							this.$store.commit('SET_ERROR', error);
						}
						await this.$store.dispatch('getCart');
						this.$store.commit('SET_PAYMENT', {
							...this.payment,
							failureCode: errorDetail.account ? 'invalid email' : errorDetail.code
						});
						throw error;
					}
				},
				recomputeOfferProperty: () => {
					// Re-compute shouldGetOffer property
					this.$store.commit('SET_OFFER', {
						offerCookie: this.cookieStorage.getItem(this.offerCookieName),
						isSmallDevice: this.isSmallDevice
					});
				},
				showOfferModal: () => {
					this.$store.commit('SET_MODAL', 'offer');
					this.cookieStorage.setItem(this.offerCookieName, this.app.session?.id, {
						path: '/'
					});
					this.utils().recomputeOfferProperty();
				},
				hideModal: () => {
					this.$store.commit('SET_MODAL', '');
				},
				showPayconiqModal: (paymentDetails) => {
					this.$store.commit('SET_PAYMENT', {
						...this.payment,
						onlinePayment: {
							// Payconiq
							payconiqUrl: paymentDetails.urlIntent,
							payconiqQrCode: paymentDetails.qrCodeData
						}
					});
					this.$store.commit('SET_MODAL', 'payconiq');
				}
			};
		}
	},
	template
};
